import React, { useEffect, useState } from "react";
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Divider, MenuItem, Snackbar,
  TextField, Tooltip, Typography,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import BlockIcon from "@mui/icons-material/Block";
import CancelIcon from "@mui/icons-material/Cancel";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import GroupsIcon from "@mui/icons-material/Groups";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  contactPortalAgentService,
  ContactPortalAgentStatus,
  ContactPortalInviteResponse,
  AgentSpouseSharing,
  EligibleFamilyMember,
} from "../../../services/crm/contactPortalAgentService";

export interface FamilyInfoForCard {
  familyId: number;
  familyName: string;
  siblingContacts: EligibleFamilyMember[];
}

interface Props {
  contactId: number;
  familyInfo?: FamilyInfoForCard;
  // The family_portal_consent row is shared between both contacts in the pair — when this card
  // enables/clears sharing, a sibling card showing the OTHER contact of that same pair (e.g. the
  // side-by-side view in ConsultingContactInfo) is looking at the same row and must refresh too.
  onFamilySharingChanged?: () => void;
  // Drives the new invitation link's expiry: PRE_ADVICE links expire in 3 months, REGULAR links
  // are effectively permanent. Comes from UserConsultingPage's period toggle, threaded down.
  selectedPeriodType?: "REGULAR" | "PRE_ADVICE";
}

const STATUS_LABELS: Record<string, { label: string; color: "success" | "default" | "warning" | "error" }> = {
  active: { label: "פעיל", color: "success" },
  pending_registration: { label: "ממתין לרישום", color: "warning" },
  revoked: { label: "בוטל", color: "error" },
  expired: { label: "פג תוקף", color: "warning" },
  completed: { label: "הושלם", color: "default" },
  cancelled: { label: "לינק הרשמה בוטל", color: "error" },
};

function resolveStatusKey(status: ContactPortalAgentStatus): string {
  if (status.status === "active" && !status.is_passkey_registered) return "pending_registration";
  return status.status;
}

function formatExpiry(isoString: string): string {
  const d = new Date(isoString);
  return d.toLocaleString("he-IL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export default function ContactPortalAccessCard({ contactId, familyInfo, onFamilySharingChanged, selectedPeriodType }: Props) {
  const [status, setStatus] = useState<ContactPortalAgentStatus | null>(null);
  const [newInvite, setNewInvite] = useState<ContactPortalInviteResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<string | null>(null);
  const [revokeDialog, setRevokeDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extendDialog, setExtendDialog] = useState(false);
  const [extendDate, setExtendDate] = useState("");
  const [extendLoading, setExtendLoading] = useState(false);

  const [spouseSharing, setSpouseSharing] = useState<AgentSpouseSharing | null>(null);
  const eligibleMembers: EligibleFamilyMember[] = familyInfo?.siblingContacts ?? [];
  const [sharingLoading, setSharingLoading] = useState(false);
  const [selectedSharingKey, setSelectedSharingKey] = useState<string>("");
  const [spousePortalStatus, setSpousePortalStatus] = useState<ContactPortalAgentStatus | null>(null);

  useEffect(() => {
    loadStatus();
    loadFamilySharing();
  }, [contactId]);

  // Exactly one eligible member — no need to make the agent choose from a dropdown of one.
  const onlyEligibleMember = eligibleMembers.length === 1 ? eligibleMembers[0] : null;

  const selectedMember = selectedSharingKey
    ? eligibleMembers.find((m) => `${m.id}:${m.family_id}` === selectedSharingKey)
    : null;
  const selectedMemberName = selectedMember
    ? `${selectedMember.first_name} ${selectedMember.last_name}`.trim()
    : spouseSharing?.spouse_name ?? "בן/בת המשפחה";

  const isPairingSaved =
    !!selectedSharingKey &&
    !!spouseSharing?.spouse_contact_id &&
    selectedSharingKey === `${spouseSharing.spouse_contact_id}:${spouseSharing.family_id}`;

  // Combined display status for the saved pairing, straight from the row's pairing_status()
  // (revoking is mutual, so "revoked" always means both directions are cut off — see
  // revoke_my_sharing).
  const combinedSharingStatus: { label: string; color: "success" | "warning" | "error" } | null = !isPairingSaved
    ? null
    : spouseSharing?.pairing_status === "revoked"
      ? {
          label: spouseSharing.revoked_by_name
            ? `שיתוף מידע משפחתי בוטל (על ידי ${spouseSharing.revoked_by_name})`
            : "שיתוף מידע משפחתי בוטל",
          color: "error",
        }
      : spouseSharing?.pairing_status === "active"
        ? { label: "שיתוף מידע משפחתי פעיל", color: "success" }
        : { label: "שיתוף מידע משפחתי ממתין לאישור", color: "warning" };

  useEffect(() => {
    if (onlyEligibleMember && !selectedSharingKey) {
      setSelectedSharingKey(`${onlyEligibleMember.id}:${onlyEligibleMember.family_id}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyEligibleMember]);

  // Once a family member is selected (paired or not yet saved), show whether THEY already have a
  // registered/active portal link — the pairing is shared/symmetric, so this is relevant context
  // regardless of which of the two contacts' cards the agent is viewing.
  useEffect(() => {
    const [spouseIdStr] = selectedSharingKey ? selectedSharingKey.split(":") : [null];
    if (!spouseIdStr) {
      setSpousePortalStatus(null);
      return;
    }
    contactPortalAgentService
      .getStatus(Number(spouseIdStr))
      .then(setSpousePortalStatus)
      .catch(() => setSpousePortalStatus(null));
  }, [selectedSharingKey]);

  const loadStatus = () => {
    setLoading(true);
    setNewInvite(null);
    contactPortalAgentService
      .getStatus(contactId)
      .then(setStatus)
      .catch(() => setError("שגיאה בטעינת סטטוס הפורטל"))
      .finally(() => setLoading(false));
  };

  const loadFamilySharing = () => {
    contactPortalAgentService
      .getFamilySharing(contactId)
      .then((sharing) => {
        setSpouseSharing(sharing);
        // Only sync from the server when there's an actual saved pairing — an empty result here
        // must not clobber a selection the agent has already made (or the auto-selected single
        // eligible member) before this async call resolved.
        if (sharing.spouse_contact_id && sharing.family_id) {
          setSelectedSharingKey(`${sharing.spouse_contact_id}:${sharing.family_id}`);
        }
      })
      .catch(() => {
        // best-effort — family sharing status is non-critical to the rest of the card
      });
  };

  const handleSaveFamilySharing = async () => {
    setSharingLoading(true);
    try {
      const [spouseIdStr, familyIdStr] = selectedSharingKey ? selectedSharingKey.split(":") : [null, null];
      const spouseContactId = spouseIdStr ? Number(spouseIdStr) : null;
      const familyId = familyIdStr ? Number(familyIdStr) : null;
      const result = await contactPortalAgentService.setFamilySharing(contactId, spouseContactId, familyId);
      setSpouseSharing(result);
      setSnackbar(spouseContactId ? "שיתוף משפחתי הופעל" : "שיתוף משפחתי בוטל");
      onFamilySharingChanged?.();
    } catch {
      setError("שגיאה בעדכון שיתוף משפחתי");
    } finally {
      setSharingLoading(false);
    }
  };

  const handleClearFamilySharing = async () => {
    setSharingLoading(true);
    try {
      const result = await contactPortalAgentService.setFamilySharing(contactId, null, null);
      setSpouseSharing(result);
      setSelectedSharingKey("");
      setSnackbar("שיתוף משפחתי בוטל");
      onFamilySharingChanged?.();
    } catch {
      setError("שגיאה בביטול שיתוף משפחתי");
    } finally {
      setSharingLoading(false);
    }
  };

  const handleCopyLink = async (link: string, label: string) => {
    await navigator.clipboard.writeText(link);
    setSnackbar(`${label} הועתק ללוח`);
  };

  const handleInvite = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const res = await contactPortalAgentService.createInvitation(contactId, selectedPeriodType ?? "REGULAR");
      setNewInvite(res);
      await navigator.clipboard.writeText(res.invitation_link);
      setSnackbar("לינק הרישום הועתק ללוח");
      loadStatus();
    } catch {
      setError("שגיאה ביצירת הזמנה");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevoke = async () => {
    setRevokeDialog(false);
    setActionLoading(true);
    try {
      await contactPortalAgentService.revokePortal(contactId);
      setSnackbar("גישת הפורטל בוטלה");
      loadStatus();
    } catch {
      setError("שגיאה בביטול הגישה");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelInvitation = async () => {
    setActionLoading(true);
    setError(null);
    try {
      await contactPortalAgentService.cancelInvitation(contactId);
      setSnackbar("לינק ההזמנה בוטל");
      loadStatus();
    } catch {
      setError("שגיאה בביטול לינק ההזמנה");
    } finally {
      setActionLoading(false);
    }
  };

  const openExtendDialog = () => {
    const current = status?.expires_at ? new Date(status.expires_at) : new Date();
    setExtendDate(current.toISOString().slice(0, 10));
    setExtendDialog(true);
  };

  const handleExtendExpiry = async () => {
    if (!extendDate) return;
    setExtendLoading(true);
    try {
      await contactPortalAgentService.updateExpiry(contactId, new Date(extendDate).toISOString());
      setExtendDialog(false);
      setSnackbar("תוקף הלינק עודכן");
      loadStatus();
    } catch {
      setError("שגיאה בעדכון תוקף הלינק");
    } finally {
      setExtendLoading(false);
    }
  };

  const chipProps = status ? (STATUS_LABELS[resolveStatusKey(status)] ?? { label: status.status, color: "default" as const }) : null;
  const hasActivePasskey = status?.is_passkey_registered && status.status === "active";

  // Prefer freshly-created invite data; fall back to status data
  const invitationLink = newInvite?.invitation_link ?? status?.invitation_link;
  const invitationExpiresAt = newInvite?.invitation_expires_at ?? status?.invitation_expires_at;
  const portalLink = newInvite?.portal_link ?? status?.portal_link;

  return (
    <>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              פורטל לקוח
            </Typography>
            {status && chipProps && (
              <Chip label={chipProps.label} color={chipProps.color} size="small" />
            )}
          </Box>

          {loading && <CircularProgress size={20} />}
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

          {status && !loading && (
            <>
              <Box sx={{ mb: 1.5 }}>
                {status.is_passkey_registered ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <FingerprintIcon fontSize="small" color="success" />
                    <Typography variant="body2" color="success.main">
                      Passkey רשום ({status.registered_device_count} מכשירים)
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">Passkey לא רשום עדיין</Typography>
                )}

                {status.last_login_at && (
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                    כניסה אחרונה: {new Date(status.last_login_at).toLocaleString("he-IL")}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ mb: 1.5 }} />

              {/* Invitation link (registration) — shown only before passkey is registered */}
              {!hasActivePasskey && invitationLink && invitationExpiresAt && (
                <Box sx={{ mb: 1.5, p: 1.5, bgcolor: "warning.50", borderRadius: 1, border: "1px solid", borderColor: "warning.200" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                    <AccessTimeIcon fontSize="small" color="warning" />
                    <Typography variant="body2" fontWeight={600} color="warning.dark">
                      לינק הרשמה ראשונית
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    לינק זה תקף עד {formatExpiry(invitationExpiresAt)} (8 שעות בלבד). לאחר שהלקוח ירשם — הלינק יפוג ולא ניתן יהיה לרשום Passkey חדש ממנו.
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => handleCopyLink(invitationLink, "לינק הרישום")}
                    >
                      העתק לינק רישום
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={handleCancelInvitation}
                      disabled={actionLoading}
                    >
                      בטל לינק
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Permanent portal link (login) — only meaningful once a passkey is registered;
                  before that, the invitation link above is the only way in. */}
              {hasActivePasskey && portalLink && (
                <Box sx={{ mb: 1.5, p: 1.5, bgcolor: "grey.50", borderRadius: 1, border: "1px solid", borderColor: "grey.200" }}>
                  <Typography variant="body2" fontWeight={600} mb={0.5}>
                    לינק כניסה קבוע
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    לינק זה קבוע — הלקוח ישתמש בו לכניסות עתידיות עם ה-Passkey שלו.
                    {status?.expires_at && ` תוקף עד ${formatExpiry(status.expires_at)}.`}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => handleCopyLink(portalLink, "לינק הכניסה")}
                    >
                      העתק לינק כניסה
                    </Button>
                    <Button size="small" variant="text" onClick={openExtendDialog}>
                      ערוך תוקף
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Expired link — agent needs to extend before the client can use it again */}
              {status?.status === "expired" && (
                <Box sx={{ mb: 1.5, p: 1.5, bgcolor: "warning.50", borderRadius: 1, border: "1px solid", borderColor: "warning.200" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                    <AccessTimeIcon fontSize="small" color="warning" />
                    <Typography variant="body2" fontWeight={600} color="warning.dark">
                      תוקף הלינק פג
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    {status.expires_at && `הלינק פג תוקף ב-${formatExpiry(status.expires_at)}. `}
                    הלקוח אינו יכול להיכנס לפורטל עד שהתוקף יוארך.
                  </Typography>
                  <Button size="small" variant="outlined" color="warning" onClick={openExtendDialog}>
                    הארך תוקף
                  </Button>
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {!hasActivePasskey && (
                  <Tooltip title={invitationLink && invitationExpiresAt ? "מבטל את הלינק הקיים ויוצר לינק הרשמה חדש (תוקף 8 שעות)" : "יוצר לינק לרישום Passkey ראשון (תוקף 8 שעות)"}>
                    <span>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={actionLoading ? <CircularProgress size={14} /> : <LinkIcon />}
                        onClick={handleInvite}
                        disabled={actionLoading}
                      >
                        {invitationLink && invitationExpiresAt ? "יצור לינק חדש" : "צור לינק לרישום ראשוני"}
                      </Button>
                    </span>
                  </Tooltip>
                )}

                {hasActivePasskey && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<BlockIcon />}
                    onClick={() => setRevokeDialog(true)}
                    disabled={actionLoading}
                  >
                    בטל גישה
                  </Button>
                )}
              </Box>

              {eligibleMembers.length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                    <GroupsIcon fontSize="small" color="action" />
                    <Typography variant="body2" fontWeight={600}>
                      שיתוף עם בן/בת משפחה
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    בחירה כאן פותחת לשני הצדדים את האפשרות לשתף נתונים זה עם זה בפורטל. כל צד מבצע
                    את פעולת השיתוף בעצמו, וכל כיוון עצמאי ומיידי — ברגע שצד אחד משתף, בן/בת
                    הזוג שלו/שלה כבר יכולים לראות את הנתונים שלו/שלה, גם אם טרם שיתפו בחזרה.
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                    {onlyEligibleMember ? (
                      <Typography variant="body2" fontWeight={500}>
                        {`${onlyEligibleMember.first_name} ${onlyEligibleMember.last_name}`.trim()}
                      </Typography>
                    ) : (
                      <TextField
                        select
                        size="small"
                        label="בן/בת משפחה"
                        value={selectedSharingKey}
                        onChange={(e) => setSelectedSharingKey(e.target.value)}
                        sx={{ minWidth: 220 }}
                      >
                        <MenuItem value="">
                          <em>ללא שיתוף</em>
                        </MenuItem>
                        {eligibleMembers.map((member) => {
                          const key = `${member.id}:${member.family_id}`;
                          const label = `${member.first_name} ${member.last_name}`.trim();
                          const disambiguatedLabel =
                            eligibleMembers.filter((m) => m.id === member.id).length > 1 && familyInfo?.familyName
                              ? `${label} (${familyInfo.familyName})`
                              : label;
                          return (
                            <MenuItem key={key} value={key}>
                              {disambiguatedLabel}
                            </MenuItem>
                          );
                        })}
                      </TextField>
                    )}
                    {combinedSharingStatus ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {combinedSharingStatus.color === "success" ? (
                          <CheckCircleIcon fontSize="small" color="success" />
                        ) : combinedSharingStatus.color === "error" ? (
                          <CancelIcon fontSize="small" color="error" />
                        ) : (
                          <AccessTimeIcon fontSize="small" color="warning" />
                        )}
                        <Typography variant="body2" color={`${combinedSharingStatus.color}.main`} fontWeight={500}>
                          {combinedSharingStatus.label}
                        </Typography>
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSaveFamilySharing}
                        disabled={sharingLoading || !selectedSharingKey}
                      >
                        אפשר שיתוף מידע משפחתי
                      </Button>
                    )}
                    {spouseSharing?.spouse_contact_id && (
                      <Button size="small" color="error" onClick={handleClearFamilySharing} disabled={sharingLoading}>
                        בטל אפשרות שיתוף
                      </Button>
                    )}
                  </Box>
                  {spouseSharing?.spouse_contact_id && (
                    <>
                      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                        הלקוח רשאי כעת לשתף את הנתונים שלו עם {spouseSharing.spouse_name} בפורטל.
                      </Typography>
                      {!(spouseSharing.am_i_sharing && spouseSharing.is_spouse_sharing) && (
                        <Typography variant="caption" color="warning.main" display="block" mt={0.5}>
                          {spouseSharing.my_status === "revoked" &&
                            `הלקוח ביטל את השיתוף מצדו.`}
                          {spouseSharing.my_status !== "revoked" && spouseSharing.spouse_status === "revoked" &&
                            `${spouseSharing.spouse_name} ביטל/ה את השיתוף מצדו/ה.`}
                          {spouseSharing.my_status === "not_shared" && spouseSharing.spouse_status === "not_shared" &&
                            `אף אחד מהצדדים עדיין לא אישר שיתוף בפועל.`}
                          {spouseSharing.am_i_sharing && spouseSharing.spouse_status === "not_shared" &&
                            `הלקוח שיתף, אך ${spouseSharing.spouse_name} עדיין לא אישר/ה שיתוף בחזרה.`}
                          {spouseSharing.my_status === "not_shared" && spouseSharing.is_spouse_sharing &&
                            `${spouseSharing.spouse_name} שיתף/ה, אך הלקוח עדיין לא אישר שיתוף בחזרה.`}
                        </Typography>
                      )}
                    </>
                  )}
                  {selectedSharingKey && spousePortalStatus && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
                      {spousePortalStatus.is_passkey_registered ? (
                        <>
                          <FingerprintIcon fontSize="small" color="success" />
                          <Typography variant="caption" color="success.main">
                            {selectedMemberName} כבר רשום/ה לפורטל ויש לו/לה לינק כניסה קבוע
                          </Typography>
                        </>
                      ) : spousePortalStatus.invitation_link ? (
                        <Typography variant="caption" color="warning.main">
                          {selectedMemberName} כבר קיבל/ה לינק הרשמה לפורטל, אך עדיין לא נרשם/ה
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {selectedMemberName} עדיין לא הוזמן/ה לפורטל
                        </Typography>
                      )}
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={revokeDialog} onClose={() => setRevokeDialog(false)}>
        <DialogTitle>ביטול גישת הפורטל</DialogTitle>
        <DialogContent>
          <Typography>האם לבטל את גישת הלקוח לפורטל? הפעולה תנקה את ה-Passkey והסשן הפעיל.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeDialog(false)}>ביטול</Button>
          <Button color="error" variant="contained" onClick={handleRevoke}>בטל גישה</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={extendDialog} onClose={() => setExtendDialog(false)}>
        <DialogTitle>עריכת תוקף לינק כניסה</DialogTitle>
        <DialogContent>
          <TextField
            type="date"
            label="תוקף עד"
            value={extendDate}
            onChange={(e) => setExtendDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ mt: 1, minWidth: 220 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendDialog(false)}>ביטול</Button>
          <Button variant="contained" onClick={handleExtendExpiry} disabled={extendLoading || !extendDate}>
            {extendLoading ? <CircularProgress size={18} /> : "שמור"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
      />
    </>
  );
}
